import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
} from "@mui/material";
import { DoubleArrow } from "@mui/icons-material";
import { CustomTextBox } from "components/CustomComponents";
import { useForm } from "react-hook-form";
import { useFetch } from "services/hooks/useFetch";
import {
  CustomButtonWithBorder,
  CustomButton,
} from "components/CustomComponents/CustomButton";
import { customAPI } from "services/utils/CustomAPI";
import {
  doubleArrowStyle,
  themes,
  parentBoxStyle,
  childBoxStyleWithColor,
  infoStyle,
  toAlignEnd,
  childBoxStyle,
} from "services/constants";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { atleastOneCharacterWithSpace } from "services/constants/patterns";
import { minimum3Characters } from "services/constants/ErrorMessages";
import { CustomAutoCompleteWithLogo } from "components/CustomComponents/CustomAutoCompleteWithLogo";
import { CustomTimePicker } from "components/CustomComponents/CustomTimePicker";
import { otTimingOptions } from "pages/TeamZone/TeamZoneHeaders";
import { CustomizedDatePicker } from "components/CustomComponents/CustomizedDatePicker";
import dayjs from "dayjs";
import {
  optionsStyle,
  cardStyle,
} from "pages/HR/Shift/Configuration/ShiftCategory/Component/ShiftCategoryOptions";

export const EditPreApproval = ({
  onClose,
  preApprovalData,
  reCallApi,
  setGetId,
  setIsEditPanelOpen,
  setHandleLoader,
  setHasUnsavedChanges,
}) => {
  const { topbarCompanyId } = useSelector((state) => state.tokenReducer);
  const {
    setValue,
    handleSubmit,
    register,
    watch,
    control,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      user_name: "",
      applicable_timing: "Only Before",
      date: null,
      durationHrs: null,
      reason_for_ot: "",
      button_loader: false,
    },
  });
  const { domain, userDetails } = useSelector((state) => state.tokenReducer);
  const [initialFormValues, setInitialFormValues] = useState(null);
  const formValues = watch();
  const deepEqual = (obj1, obj2) => {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  };
  const { result: membersData } = useFetch(
    `${domain}list/employee?user_id=${userDetails?.id}&company_id=${topbarCompanyId}`
  );
  const [showAutoCompleteAvathar, setShowAutoCompleteAvathar] = useState(false);

  useEffect(() => {
    if (preApprovalData && !initialFormValues) {
      const defaultValues = {
        user_name: {
          label: preApprovalData?.employee_name,
          image: preApprovalData?.image_url,
        },
        applicable_timing: preApprovalData?.ot_consideration,
        date: preApprovalData?.ot_date
          ? dayjs(preApprovalData.ot_date.split("-").reverse().join("-"))
          : null,
        durationHrs: dayjs(preApprovalData?.ot_duration, "HH:mm"),
        reason_for_ot: preApprovalData?.reason,
        ot_id: preApprovalData?.id,
      };
      setInitialFormValues(defaultValues);
      reset(defaultValues);
    }
  }, [preApprovalData, initialFormValues, reset]);

  useEffect(() => {
    if (initialFormValues && formValues) {
      const hasChanges = !deepEqual(formValues, initialFormValues);
      setHasUnsavedChanges(hasChanges);
    }
  }, [formValues, initialFormValues, setHasUnsavedChanges]);

  const onSubmit = async (data) => {
    setValue("button_loader", true);
    const response = await customAPI(
      `${domain}Team_Zone/Approve/Over_time/ot-pre-approve/`,
      "PUT",
      {
        ot_ids: [data?.ot_id],
        ot_date: data?.date.format("YYYY-MM-DD"),
        ot_duration: dayjs(watch("durationHrs")).format("HH:mm"),
        ot_consideration: data?.applicable_timing,
        reason: data?.reason_for_ot,
        status: "Approved",
      }
    );
    if (response.ok) {
      showToast("success", response?.responseDetails?.message);
      setIsEditPanelOpen(false);
      setValue("button_loader", false);
      setGetId(preApprovalData?.id);
      setHandleLoader(true);
      reCallApi(true);
    } else {
      setValue("button_loader", false);
      showToast("error", response?.message);
    }
  };

  const updateUserName = (event, data) => {
    if (data) {
      setValue("user_name", data);
      setValue("user_id", data?.user_id);
      setShowAutoCompleteAvathar(false);
    } else {
      setShowAutoCompleteAvathar(true);
    }
  };

  const hanldeApplicableTiming = (event) => {
    setValue("applicable_timing", event.target.value);
  };

  const handleReasonForOt = (event) => {
    if (event.target.value.length <= 1024) {
      setValue("reason_for_ot", event.target.value);
    }
  };

  return (
    <Box sx={parentBoxStyle}>
      <Paper
        elevation={0}
        sx={{
          borderBottom: `1px solid ${themes?.miliedLightGray}`,
          borderRadius: 0,
        }}
      >
        <Box sx={childBoxStyleWithColor}>
          <Box sx={{ ml: 1 }}>
            <IconButton onClick={onClose} sx={doubleArrowStyle}>
              <DoubleArrow />
            </IconButton>
          </Box>

          <Box sx={{ ml: 2.2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Edit OT Pre-Approval Request
            </Typography>
            <Typography {...infoStyle}>
              Edit this OT request by updating the OT period.
            </Typography>
          </Box>
        </Box>
      </Paper>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ flexGrow: 1, overflow: "auto", p: 2 }}>
          <Box sx={childBoxStyle}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Employee Name</Typography>
              <Typography {...infoStyle}>
                Choose the employee requesting or assigned for overtime.
              </Typography>
              <CustomAutoCompleteWithLogo
                dataList={membersData?.map((obj) => ({
                  ...obj,
                  label: obj?.user_name,
                  image: obj?.image_url,
                }))}
                rules={{
                  ...register("user_name", {
                    required: "Employee name is required",
                  }),
                }}
                selectedValue={watch("user_name")}
                updateDataFunction={updateUserName}
                placeHolder="Select employee name"
                errorMessage={
                  errors?.user_name?.message ? errors?.user_name?.message : ""
                }
                textBoxError={errors?.user_name ? true : false}
                showAvathar={showAutoCompleteAvathar}
                setShowAutoCompleteAvathar={setShowAutoCompleteAvathar}
              />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}> Date</Typography>
              <Typography
                {...infoStyle}
                sx={{
                  ...infoStyle.sx,
                  mb: 0,
                }}
              >
                Select the date for the overtime.
              </Typography>
              <CustomizedDatePicker
                name="date"
                control={control}
                required={true}
                requiredMessage="Date is required"
                placeholder="Select the ot date"
                onChange={(newValue) => {
                  setValue("date", newValue);
                }}
              />
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 600 }}>Duration in Hrs</Typography>
              <Typography {...infoStyle}>
                Maximum allowed time in Hrs: as per OT Policy
              </Typography>
              <CustomTimePicker
                name="durationHrs"
                control={control}
                errors={errors}
                validateFn={(value) => {
                  if (!value || !dayjs(value).isValid()) {
                    return "Duration hours is required";
                  }
                  return true;
                }}
              />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>
                Applicable Timing
              </Typography>
              <Typography {...infoStyle}>
                Select when the overtime will be considered.
              </Typography>
              <Box>
                <RadioGroup
                  row
                  value={watch("applicable_timing")}
                  onChange={hanldeApplicableTiming}
                  sx={optionsStyle}
                >
                  {otTimingOptions.map((opt) => {
                    const isActive = watch("applicable_timing") === opt.id;
                    return (
                      <Box key={opt.id} sx={cardStyle(isActive)}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Radio value={opt.id} />
                          <Typography
                            fontWeight="medium"
                            sx={{
                              color: isActive
                                ? themes.stepActiveColor
                                : "inherit",
                            }}
                          >
                            {opt.label}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            ml: 4,
                            maxWidth: 250,
                            wordBreak: "break-word",
                          }}
                        >
                          {opt.description}
                        </Typography>
                      </Box>
                    );
                  })}
                </RadioGroup>
              </Box>
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Reason</Typography>
              <Typography {...infoStyle}>
                Mention the purpose or justification for the overtime request.
              </Typography>
              <CustomTextBox
                type="multiline"
                rules={{
                  ...register("reason_for_ot", {
                    required: "Reason for overtime is required",
                    validate: {
                      minLengthNoSpaces: (value) => {
                        if (value?.trim().length < 3 && value !== "") {
                          return minimum3Characters;
                        } else if (value?.trim().length > 255 && value !== "") {
                          return "Maximum length is 255 characters";
                        } else {
                          return true;
                        }
                      },
                      atleastOneCharacterWithSpaceReg: (value) => {
                        if (
                          atleastOneCharacterWithSpace.test(value) ||
                          value === ""
                        ) {
                          return true;
                        } else {
                          return "Atleast one alphabet required";
                        }
                      },
                    },
                  }),
                }}
                selectedValue={watch("reason_for_ot")}
                updateDataFunction={handleReasonForOt}
                placeHolder="Provide a reason for overtime"
                errorMessage={
                  errors?.reason_for_ot?.message
                    ? errors?.reason_for_ot?.message
                    : ""
                }
                textBoxError={errors?.reason_for_ot ? true : false}
              />
            </Box>

            <Box sx={toAlignEnd}>
              <CustomButtonWithBorder
                actionName="Cancel"
                typeName="button"
                actionFuntion={() => onClose()}
              />
              <CustomButton
                actionFuntion={handleSubmit(onSubmit)}
                actionName={
                  preApprovalData?.status === "Approved"
                    ? "Update"
                    : "Update & Approve"
                }
                typeName="submit"
                disableAction={watch("button_loader")}
              />
            </Box>
          </Box>
        </Box>
      </form>
    </Box>
  );
};
